import type { IJobApplication } from '../../stores/JobApplicationStore/JobApplication';
import {
  updateApplicationsStatus,
  type StatusChangeLocation,
} from '../../graphql/hirer/updateApplicationsStatus';
import type {
  Bucket,
  SearchFilters,
} from '../../stores/JobApplicationStore/types';
import { getDashisedBucket } from '../../constants/jobApplicationStatus';
import { trackUpdateStatusEvent } from '../../utils/events';

import { sendHotjarTag } from '../../utils/hotjar';
import { HOTJAR_TAG_STATUS_CHANGE } from '../../constants/hotjar';
import type { IJobApplicationStore } from '../../stores/JobApplicationStore';
import useHandleError from './useHandleError';
import { sendStatusUpdateCaptureEvent } from '../../services/jobApplicationService/sendStatusUpdateCaptureEvent';
import type { IStore } from '../../stores/Store';
import { updateIntercomReferenceCheck } from 'src/modules/intercom';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import useHandleUndoStatusChange from './useHandleUndoStatusChange';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import mapBucketTypeToBuckets from 'src/stores/JobApplicationStore/utils/mapBucketToBuckets';
import type { useToast } from 'braid-design-system';
import type { ApplicationBucket } from '../AppPageBlock/graphql/hirer-types.generated';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { convertToIsVerifiedId } from 'src/utils/eventMappers';
import type { FiltersEventCaptureData } from 'src/services/jobApplicationService/buildCaptureEventData';
import { sleep } from 'src/utils/sleep';

export const updateStatusOnServer = async (
  jobApplicationStore: IJobApplicationStore,
  jobApplication: IJobApplication,
  destinationBucket: Bucket,
  statusChangeLocation: StatusChangeLocation,
) =>
  updateApplicationsStatus(
    jobApplicationStore.positionId?.toString(),
    destinationBucket as ApplicationBucket,
    statusChangeLocation,
    [
      {
        prospectId: jobApplication.id,
        positionInList: jobApplication.positionInList,
      },
    ],
  );

interface Props {
  jobApplicationStore: IJobApplicationStore;
  jobApplication: IJobApplication;
  destinationBucket: Bucket;
  statusChangeLocation: StatusChangeLocation;
  showToast: ReturnType<typeof useToast>;
  onUpdateSuccess?: () => void;
  showSuccessMessageToast?: boolean;
  store: IStore;
  isExpanded?: boolean;
}

export interface UseHandleStatusChangeResponse {
  handleStatusChange: ({
    jobApplicationStore,
    jobApplication,
    store,
    destinationBucket,
    statusChangeLocation,
    showToast,
    onUpdateSuccess,
    showSuccessMessageToast,
  }: Props) => Promise<void>;
}

const useHandleStatusChange = ({
  filters,
  filtersEventCaptureData,
}: {
  filters: SearchFilters;
  filtersEventCaptureData: FiltersEventCaptureData;
}): UseHandleStatusChangeResponse => {
  const { t } = useTranslations(translations);
  const { handleError } = useHandleError();
  const { handleUndoStatusChange } = useHandleUndoStatusChange();
  const { getLabelFromBucket } = useBucketLabel();
  const { language } = useAppConfig();

  const showSuccessMessage = ({
    jobApplicationStore,
    showToast,
    jobApplication,
    currentBucket,
    destinationBucket,
    undoTrackUpdateStatus,
  }: {
    jobApplicationStore: IJobApplicationStore;
    showToast: ReturnType<typeof useToast>;
    jobApplication: IJobApplication;
    currentBucket: Bucket;
    destinationBucket: Bucket;
    undoTrackUpdateStatus: () => void;
  }) => {
    showToast({
      closeLabel: t('Close'),
      tone: 'positive',
      message: t('Moved to', {
        firstName: jobApplication.firstName,
        newStatusLabel: getLabelFromBucket(
          mapBucketTypeToBuckets(destinationBucket),
        ),
      }),
      key: 'STATUS_CHANGE_TOAST_KEY',
      action: {
        label: t('Undo'),
        onClick: () =>
          handleUndoStatusChange({
            jobApplicationStore,
            currentBucket: destinationBucket,
            destinationBucket: currentBucket,
            showToast,
            undoTrackUpdateStatus,
          }),
      },
    });
  };

  return {
    handleStatusChange: async ({
      jobApplicationStore,
      jobApplication,
      store,
      destinationBucket,
      statusChangeLocation,
      showToast,
      onUpdateSuccess,
      showSuccessMessageToast = true,
      isExpanded,
    }: Props) => {
      sendHotjarTag(HOTJAR_TAG_STATUS_CHANGE);

      const currentBucket = jobApplication.bucket;

      jobApplication.setIsChangingState(true);

      const success = await updateStatusOnServer(
        jobApplicationStore,
        jobApplication,
        destinationBucket,
        statusChangeLocation,
      );

      if (onUpdateSuccess) {
        onUpdateSuccess();
        // FIXME: this sleep is required here because there are multiple updates when updating status happens:
        // 1. AppPageBlock is updated by MobX's `observer` function
        // 2. Route is updated by changing of search params (inside onUpdateSuccess)
        //
        // (1.) runs first, and when it does, it still has the old query params with `&selected=<id>` which causes
        // the profile drawer to re-open when `store.jobApplicationStore.updateFromQuery` is called in AppPageBlock
        // Because of this, the drawer re-opens after it has been closed
        // Reported: https://seekchat.slack.com/archives/C24PK4XV3/p1719356837551939
        await sleep(500);
      }

      jobApplication.setIsChangingState(false);

      if (!success) {
        handleError(
          showToast,
          jobApplication,
          'updateStatusForProspects returned success: false',
        );
        return;
      }

      sendStatusUpdateCaptureEvent({
        statusChangeLocation,
        store,
        jobApplications: [jobApplication],
        destinationBucket,
        filters: filtersEventCaptureData,
      });

      jobApplication.setStatus(mapBucketTypeToBuckets(destinationBucket));

      jobApplication.setReadWithCountUpdated();

      jobApplicationStore.updateBucketCount(currentBucket, destinationBucket);
      jobApplicationStore.updateFiltersCount(jobApplication, 'RemoveJobApp');

      jobApplicationStore.topUpCandidateListWithCachedJobApplications({
        filters,
        language,
      });

      // We can remove this if statement and make the isExpanded prop mandatory when we launch to ANZ.
      const trackingProps = {
        applicationStatus: getDashisedBucket(destinationBucket),
        candidateId: jobApplication.candidateId,
        bucket: getDashisedBucket(currentBucket),
        prospectId: jobApplication.id,
        isVerifiedId: convertToIsVerifiedId(jobApplication.profile),
      };

      const undoTrackUpdateStatus = () => {
        trackUpdateStatusEvent({
          ...trackingProps,
        });
      };

      if (isExpanded !== undefined) {
        trackUpdateStatusEvent({
          ...trackingProps,
          candidateCardStatus: isExpanded ? 'Expand' : 'Collapse',
        });
      } else {
        trackUpdateStatusEvent({
          ...trackingProps,
          candidateCardStatus: 'No RR',
        });
      }
      if (showSuccessMessageToast) {
        showSuccessMessage({
          jobApplicationStore,
          showToast,
          jobApplication,
          currentBucket,
          destinationBucket,
          undoTrackUpdateStatus,
        });
      }

      if (destinationBucket === 'SHORTLIST') {
        updateIntercomReferenceCheck();
      }

      jobApplicationStore.updateListStateAfterChangeStatus(jobApplication.id);
    },
  };
};

export default useHandleStatusChange;
